<template>
  <main>
    <b-container v-if="project">
      <div class="page-breadcrumb">
        <router-link :to="{ name: 'ProjectIndex' }">
          <i class="fa fa-angle-left" /> Back to projects
        </router-link>
      </div>

      <div class="section-header mb-5">
        <div class="w-50">
          <div class="d-flex align-items-center">
            <h1 class="heading">{{ project.name }}</h1>
            <b-badge class="ml-2" variant="light">{{ project.tag }}</b-badge>
          </div>
          <div>
            <small>
              <strong>Created on:</strong>
              {{ project.creationDate | moment("DD-MM-YYYY HH:mm") }}
            </small>
            <small class="mx-2">|</small>
            <small>
              <strong>Last modified on:</strong>
              {{ project.lastModified | moment("DD-MM-YYYY HH:mm") }}
            </small>
            <br />
            <small>
              {{ project.description }}
            </small>
          </div>
        </div>

        <div v-if="isAuthenticatedUserHasAccess" class="actions">
          <div class="action" v-if="project.user_allowed.includes('Owner')">
            <b-btn
              variant="outline-secondary"
              class="mr-1"
              @click="confirmDeleteProject"
            >
              <i class="fa fa-trash" /> Delete project
            </b-btn>
          </div>
          <div class="action" v-if="!project.user_allowed.includes('Owner')">
            <b-btn
              variant="outline-secondary"
              class="mr-1"
              @click="confirmLeaveProject()"
            >
              <i class="fa fa-sign-out-alt" /> Leave project
            </b-btn>
          </div>
          <div class="action" v-if="project.user_allowed.includes('Owner')">
            <b-btn
              variant="primary"
              @click="
                $router.push({
                  name: 'ProjectEdit',
                  params: { id: project._id },
                })
              "
            >
              <i class="fa fa-pencil-alt" /> Edit project
            </b-btn>
          </div>
        </div>
      </div>

      <!-- <div class="layoutcard">
        <table class="table table-hover vertical-align-middle">
          <tbody>
            <tr>
              <th>Description</th>
              <td>{{ project.description }}</td>
            </tr>
            <tr>
              <th>Created on</th>
              <td>
                {{ project.creationDate | moment("DD-MM-YYYY HH:mm") }}
              </td>
            </tr>
            <tr>
              <th>Last modified on</th>
              <td>
                {{ project.lastModified | moment("DD-MM-YYYY HH:mm") }}
              </td>
            </tr>
          </tbody>
        </table>
      </div> -->

      <div class="section-header">
        <h1 class="heading">Models</h1>

        <div v-if="isAuthenticatedUserHasAccess" class="actions">
          <div class="action">
            <b-btn
              variant="primary"
              @click="isModelAllowed"
              :disabled="!project.user_allowed.includes('Owner')"
            >
              <i class="fa fa-plus" /> Add model
            </b-btn>
            <CustomAlert
              :visible="showAlert"
              title="Limit Exceeded"
              message="You have reached the usage limit of your company subscription plan.
              To extend it, please contact us at info@mynd-ware.com"
              @close="showAlert = false"
              />
          </div>
        </div>
      </div>

      <div class="layoutcard">
        <table
          class="table table-hover vertical-align-middle"
          v-if="modelsLoading"
        >
          <thead>
            <tr>
              <th>
                <div class="placeholder">
                  <div class="placeholder__tile"></div>
                </div>
              </th>
              <th>
                <div class="placeholder">
                  <div class="placeholder__tile"></div>
                </div>
              </th>
              <th class="text-center">
                <div class="placeholder">
                  <div class="placeholder__tile"></div>
                </div>
              </th>
              <th>
                <div class="placeholder">
                  <div class="placeholder__tile"></div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colspan="12">
                <div class="placeholder">
                  <div class="placeholder__tile"></div>
                  <div class="placeholder__tile"></div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <table class="table table-hover vertical-align-middle" v-else>
          <thead>
            <tr>
              <th>{{ isAuthenticatedUserHasAccess? "Model &amp; Pools":"Models" }}</th>
              <th>Owner</th>
              <th class="text-center">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <ProjectViewModelRow
              v-for="model in projectModels"
              :key="model._id"
              :model="model"
              :project="project"
              :userOptions="userOptions"
              :discoverOptions="discoverOptions"
              @fetchProject="fetchProject"
              @fetchProjectModels="fetchProjectModels"
              @pauseFetchProject="pauseFetchProject"
              @resumeFetchProject="resumeFetchProject"
            ></ProjectViewModelRow>
          </tbody>
        </table>
      </div>

      <div v-if="isAuthenticatedUserHasAccess" class="section-header">
        <h1 class="heading">Users</h1>

        <div class="actions">
          <div class="action">
            <b-dropdown
              v-if="projectUsers.length > 0"
              id="dropdown-1"
              variant="primary"
              :disabled="!project.user_allowed.includes('Owner')"
              right
            >
              <template #button-content>
                <i class="fa fa-plus" /> Add User
              </template>
              <b-dropdown-item
                v-for="u in userOptions"
                :key="u._id"
                @click="addUserSubmit(u)"
                >{{ u.user_name }}</b-dropdown-item
              >
            </b-dropdown>
          </div>
        </div>
      </div>

      <div v-if="isAuthenticatedUserHasAccess" class="layoutcard">
        <b-table
          :show-empty="true"
          :items="projectUsers"
          :fields="fields"
          :sort-by="'user_name'"
          class="table-hover vertical-align-middle"
        >
          <template v-slot:cell(user_name)="data">
            <router-link
              :to="{ name: 'UserView', params: { id: data.item._id } }"
              >{{ data.item.user_name }}</router-link
            >
          </template>
          <template v-slot:cell(user_role)="data">
            <b-badge variant="light">{{
              project.users[0].user === data.item.user_name ? "Owner" : "User"
            }}</b-badge>
          </template>
          <template v-slot:cell(actions)="data">
            <b-btn-group>
              <b-btn
                variant="outline-secondary"
                size="sm"
                @click="confirmDeleteUser(data.item._id)"
                :disabled="
                  !project.user_allowed.includes('Owner') ||
                  project.users[0].user === data.item.user_name
                "
              >
                <i class="fa fa-trash" />
              </b-btn>
            </b-btn-group>
          </template>
        </b-table>
      </div>
    </b-container>
  </main>
</template>

<script>
import _ from "lodash";
import { setTimeout } from "timers";
import { mapActions, mapGetters, mapState } from "vuex";
import InitAdmin from "../Shared/InitAdmin";
import UserSelectModal from "../Shared/UserSelectModal";
import ProjectViewModelRow from "./ProjectViewModelRow";
import CustomAlert from "../Shared/CustomAlert.vue";

export default {
  name: "ProjectView",
  components: {
    ProjectViewModelRow,
    UserSelectModal,
    CustomAlert
  },
  extends: InitAdmin,
  data() {
    return {
      showAlert: false,
      interval: null,
      modelInterval: null,
      project: null,
      projectModels: null,
      modelsLoading: true,
      fields: [
        {
          key: "user_name",
        },
        {
          key: "user_role",
        },
        {
          key: "full_name",
        },
        {
          key: "email",
        },
        {
          key: "created",
        },
        {
          key: "actions",
          tdClass: "text-right",
          label: "",
        },
      ],
    };
  },
  computed: {
    ...mapState({
      apiId: (state) => state.user.apiId,
      users: (state) => state.user.users,
      userRole: (state) => state.auth.userRole,
      userModels: (state) => state.auth.userModels,
      projectUsers: (state) => state.project.projectUsers,
      realmSettings: (state) => state.setting.realmSettings
    }),
    ...mapGetters(["isAuthenticatedUserHasAccess", "authenticatedUserModels"]),
    userOptions() {
      // Filter out users that are already in the project
      const selection = this.users.filter(
        (user) => !this.projectUsers.some((u) => u._id === user._id)
      );
      return _.sortBy(selection, "username");
      //return _.sortBy(this.users, "username");
    },
    discoverOptions() {
      // Filter out users that are in the project and have viewonly role
      return this.projectUsers.filter(u => u.role === 'discover');
    }
  },
  mounted() {
    this.fetchProject();
    //this.interval = setInterval(this.fetchProject, 10000);
  },
  destroyed() {
    if(this.interval) { clearInterval(this.interval); }
  },
  methods: {
    ...mapActions([
      "addUserToProject",
      "deleteProject",
      "leaveProject",
      "loadProject",
      "removeUserFromProject",
      "loadProjectModels",
      "processModel",
      "loadProjectUsers"
    ]),
    async fetchProject() {
      this.loadProject({ id: this.$route.params.id }).then(async (res) => {
        this.project = res;
        await this.fetchProjectModels();
        this.loadProjectUsers({ id: this.$route.params.id });
        if(this.projectModels.find(m => m.status.code === 2 )){
          this.fetchProjectModels();
          this.interval = setInterval(this.fetchProjectModels, 10000);
        }
      });
    },
    isModelAllowed(){
      if (this.realmSettings.total_models_created == parseInt(this.realmSettings.maxAllModelsByProject) || this.realmSettings.total_models_created > parseInt(this.realmSettings.maxAllModelsByProject) ){
        this.showAlert = true;
        return;
      }
      this.$router.push({name: 'ModelCreate',params: { projectId: this.project._id }})
    },
    pauseFetchProject() {
      clearInterval(this.interval);
    },
    resumeFetchProject() {
      this.interval = setInterval(this.fetchProjectModels, 10000);
    },
    async fetchProjectModels() {
      return new Promise((resolve, reject) => {
        this.loadProjectModels({ id: this.project._id }).then(async (res) => {
          if(res.find(m => m.status.code === 2 ) === undefined){
            clearInterval(this.interval);
          }
          this.modelsLoading = false;
          if(this.userRole === 'discover' && this.userModels.length > 0){
            this.projectModels = res.filter(m => this.userModels.includes(m._id));
          }
          else{
            this.projectModels = res;
          }
          resolve();
        });
      })
    },
    fetchProjectUsers(){
      this.loadProjectUsers({ id: this.$route.params.id });
    },
    confirmDeleteProject() {
      this.$bvModal
        .msgBoxConfirm(
          `Are you sure you want to delete the project “${this.project.name}”?`,
          this.$confirmOptions
        )
        .then((value) => {
          if (value) {
            this.deleteProject({ projectId: this.project._id }).then(() => {
              this.$toasted.show("Project deleted", { type: "error" });
              setTimeout(() => {
                this.$router.push({ name: "ProjectIndex" });
              }, 1000);
            });
          }
        });
    },
    confirmDeleteUser(userId) {
      this.$bvModal
        .msgBoxConfirm(
          `Are you sure you want to remove "${this.getUserInfo(
            userId
          )}" from this project?`,
          this.$confirmOptions
        )
        .then((value) => {
          if (value) {
            this.removeUserFromProject({
              projectId: this.project._id,
              userId: userId,
            }).then(() => {
              this.fetchProjectUsers();
              this.$toasted.show("User removed from project");
              /*this.loadProject({ id: this.$route.params.id }).then((res) => {
                this.project = res;
              });*/
            });
          }
        });
    },
    confirmLeaveProject() {
      this.$bvModal
        .msgBoxConfirm(
          `Are you sure you want to leave the project “${this.project.name}”?`,
          this.$confirmOptions
        )
        .then((value) => {
          if (value) {
            this.leaveProject({ project_id: this.project._id }).then(() => {
              this.$toasted.show(`Left project "${this.project.name}"`, { type: "success" });
              setTimeout(() => {
                this.$router.push({ name: "ProjectIndex" });
              }, 1000);
            });
          }
        });
    },
    getUserInfo(userId) {
      const user = this.users.find((u) => u._id === userId);
      return user ? user.full_name : null;
    },
    addUserSubmit(user) {
      this.addUserToProject({
        projectId: this.project._id,
        userId: user._id,
      }).then(() => {
        this.fetchProjectUsers();
        this.$toasted.show("User added to project");
        //this.loadProject({ id: this.$route.params.id }).then(res => {
        //  this.project = res;
        //});
      });
    },
  },
};
</script>
